'use client';

import { c as _c } from "react/compiler-runtime";
import type { db } from '@funkey/api/src';
import { hexToRgb } from '@funkey/shared';
import React from 'react';

// --primary-light: ${branding.primaryLight ? `rgb(${branding.primaryLight})` : 'green'};
// --background: ${branding.background ? `rgb(${branding.background})` : 'blue'};
// --accent: ${branding.accent ? `rgb(${branding.accent})` : 'blue'};
// --accent-light: ${branding.accentLight ? `rgb(${branding.accentLight})` : 'blue'};
// --text: ${branding.headerText ? `rgb(${branding.headerText})` : 'blue'};
// --header-text: ${branding.headerText ? `rgb(${branding.headerText})` : 'blue'};
// --button: ${branding.button ? `rgb(${branding.button})` : 'blue'};

// props: { settings: AdminBusiness['brandingColors'] }
export const BizzStyles: React.FC<{
  settings: typeof db.businesses.$inferSelect.brandingColors;
}> = props => {
  const $ = _c(5);
  if (!props.settings) {
    return null;
  }
  let t0;
  if ($[0] !== props.settings) {
    t0 = transformColors(props.settings);
    $[0] = props.settings;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const branding = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      PRIMARY: "33,63,114"
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const DEFAULTS = t1 as const;
  const t2 = `
        :root {
            ${branding.primary ? `--primary: rgb(${branding.primary});` : ""}
            ${branding.accent ? `--secondary: rgba(${branding.accent}, 1);` : ""}

            --bizz-bg: rgba(${branding.background || "255,255,255,1"});
            --bizz-primary-rgb: ${branding.primary || DEFAULTS.PRIMARY};
        }

        .header-rounding:after, 
        .header-rounding:before {
            --tw-shadow: 0 -2rem 0 0 rgb(${branding.primary}) !important;
            --tw-shadow-color: rgb(${branding.primary}) !important;
        }

        .background-custom-light {
            background-color: rgb(${branding.primaryLight}) !important;
        }

        .bg-foreground  {
            background: rgb(${branding.foreground ?? "255,255,255"}) !important;
        }

        html,
        .bizz-bg,
        #bizz-layout-main {
             background: rgb(${branding.background ?? "255,255,255"}) !important;
        }
        
        button {
            --tw-ring-color: rgba(${branding.accent}, 1) !important;
        }
     
        .focus:ring-secondary:focus {
            --tw-ring-color: rgba(${branding.accent}, 1) !important;
        }

        body, .bizz-header {
           background: rgb(${branding.primary || DEFAULTS.PRIMARY}) !important;
        }
        h1, h2, h3, h4, h5, h6 {
            color: rgb(${branding.headerText}) !important;
        }
        .first\\:text-primary,
        .text-primary {
            color: rgb(${branding.text});
        }

        /* Tertiary color is actually our primary color */
        .bg-tertiary {
             background: rgb(${branding.accent}) !important;
        }

        .bg-tertiary:hover {
            background: rgba(${branding.accent}, 0.8) !important;
        }

        /* Check if we will use secondary or accent color here */
        .bg-turqoise-500,
        .bg-secondary {
            background: rgba(${branding.accent}, 1) !important;
        }
        
        .bg-secondary:hover {
            background: rgba(${branding.accent}, 0.8) !important;
        }

        .border-secondary {
            border-color: rgba(${branding.accent}, 1) !important;
        }

        /** .hover\\:border-secondary{
            border-color: rgba(${branding.accent}, 1) !important;
        } */

        .hover\\:enabled\\:border-secondary {
            border-color: rgba(${branding.accent}, 1) !important;
        }
        
        .hover\\:border-secondary:hover{
            border-color: rgba(${branding.accent}, 1) !important;
        }

        .hover\\:text-secondary:hover {
            color: rgba(${branding.accent}, 1) !important;
        }
        .checked\\:border-secondary:checked {
            --tw-border-opacity: 0 !important;
        }

        .data-\\[state\\=on\\]\\:text-secondary[data-state=on],
        .text-secondary {
            color: rgba(${branding.accent}, 1) !important;
        }   

        .data-\\[state\\=on\\]\\:bg-secondary-50[data-state=on] {
            background: rgba(${branding.accent}, 0.1) !important;
        }

        /* The pressed state of a rule chip / mode card paints text + background
         * from the accent above; without these the border and the hover text
         * keep the default (green) secondary and clash with the branding. */
        .data-\\[state\\=on\\]\\:border-secondary[data-state=on] {
            border-color: rgba(${branding.accent}, 1) !important;
        }

        /* These two carry an explicit opacity in their utility name (50% and
         * 40%); grouping them with the opaque rules flattened both states. */
        .hover\\:border-secondary\\/50:hover {
            border-color: rgba(${branding.accent}, 0.5) !important;
        }

        .hover\\:text-secondary-400:hover {
            color: rgba(${branding.accent}, 1) !important;
        }

        .bg-secondary-50 {
            background: rgba(${branding.accent}, 0.1) !important;
        }

        .bg-secondary-50\\/40 {
            background: rgba(${branding.accent}, 0.4) !important;
        }
    `;
  let t3;
  if ($[3] !== t2) {
    t3 = <style>{t2}</style>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};

/** For all colors in the settings, check if we need to convert hex to rgb */
const transformColors = (settings: React.ComponentProps<typeof BizzStyles>['settings']): typeof db.businesses.$inferSelect.brandingColors => Object.fromEntries(Object.entries(settings).map(([key, value]) => [key, typeof value === 'string' && value.startsWith('#') ? hexToRgb(value) : value]));